import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { impersonationUtils } from '@utils/impersonation';
import { AsyncCombobox } from '@/components/form/async-combobox';
import { Button } from '@/components/lumen/button';
import { useLanguage } from '@contexts/LanguageContext';
import { formatTemplate } from '@/lib/format-template';

const SELECT_USER_ID = 'impersonation-select-user';

interface ImpersonationSelectProps {
  onImpersonationStart?: () => void;
  onImpersonationStop?: () => void;
}

export default function ImpersonationSelect({ onImpersonationStart, onImpersonationStop }: ImpersonationSelectProps) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationInfo, setImpersonationInfo] = useState<any>(null);

  const updateImpersonationState = () => {
    setIsImpersonating(impersonationUtils.isImpersonating());
    setImpersonationInfo(impersonationUtils.getImpersonationInfo());
  };

  useEffect(() => {
    updateImpersonationState();

    // Listen for impersonation state changes
    const handleImpersonationChange = () => {
      updateImpersonationState();
    };

    window.addEventListener('impersonationChanged', handleImpersonationChange);

    return () => {
      window.removeEventListener('impersonationChanged', handleImpersonationChange);
    };
  }, []);

  const handleStartImpersonation = async (userId: string, userObject: any) => {
    if (!userId || !userObject?.origin?.id) return;

    setLoading(true);
    try {
      await impersonationUtils.startImpersonation(userObject.origin.id, 'Admin impersonation');

      toast.success('Impersonation Started', {
        description: `Successfully started impersonating ${userObject.origin.email}`,
      });

      onImpersonationStart?.();

      // Refresh the page to ensure all components use the new impersonated context
      window.location.reload();
    } catch (error: any) {
      toast.error('Error', { description: error.message || 'Failed to start impersonation' });
    } finally {
      setLoading(false);
    }
  };

  const handleStopImpersonation = async () => {
    setLoading(true);
    try {
      await impersonationUtils.stopImpersonation();

      toast.success('Impersonation Stopped', { description: 'Successfully stopped impersonation' });

      onImpersonationStop?.();

      // Refresh the page to ensure all components use the restored original context
      window.location.reload();
    } catch (error: any) {
      toast.error('Error', { description: error.message || 'Failed to stop impersonation' });
    } finally {
      setLoading(false);
    }
  };

  if (isImpersonating && impersonationInfo) {
    const impersonatedUser = impersonationUtils.getImpersonatedUserInfo();

    return (
      <div className="rounded-card border border-ai/30 bg-ai/10 px-3 py-2">
        <div className="mb-2 font-medium text-ai-ink text-xs">
          {formatTemplate(t('lumen.frame.impersonatingSelect'), {
            target:
              impersonatedUser?.email ||
              formatTemplate(t('lumen.frame.impersonationUserId'), { id: impersonationInfo.targetUserId }),
          })}
        </div>
        {impersonatedUser?.name && <div className="mb-2 text-ai-ink text-xs">{impersonatedUser.name}</div>}
        <Button
          type="button"
          variant="destructive"
          size="xs"
          className="w-full"
          onClick={handleStopImpersonation}
          disabled={loading}
        >
          {loading ? t('lumen.frame.stopping') : t('lumen.frame.stopImpersonation')}
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-card border border-label/30 bg-label/10 px-3 py-2">
      <label htmlFor={SELECT_USER_ID} className="mb-2 block font-medium text-info-ink text-xs">
        {t('lumen.frame.impersonateUser')}
      </label>
      <AsyncCombobox
        id={SELECT_USER_ID}
        placeholder={t('lumen.frame.searchUsers')}
        dynamicKeyParam="query"
        path="/api/v1/users"
        additionalParam="orderBy=email&ascending=true"
        onChange={handleStartImpersonation}
        valueOnResponseKey="id"
        optionsLabelKeys={['email', 'name']}
        value=""
        disabled={loading}
        loadingText={t('lumen.pibEngine.lookup.loading')}
        emptyText={t('lumen.pibEngine.lookup.empty')}
        loadErrorText={formatTemplate(t('lumen.pibEngine.lookup.failed'), { label: '' })}
        // Load the user list immediately so the dropdown isn't empty until focus.
        loadOnMount
      />
    </div>
  );
}
